import { makeStyles } from 'tss-react/mui'
import { fundingColors as C } from '../../theme/fundingColors'
import { fundingFormFieldBox } from '../../theme/fundingSharedStyles'
import { fundingFont, fundingType } from '../../theme/fundingTypography'

export const useStyles = makeStyles()(() => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    width: '100%',
    fontFamily: fundingFont,
    '& .MuiTypography-root': { fontFamily: fundingFont },
  },
  gridRoot: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    width: '100%',
    fontFamily: fundingFont,
    '& .MuiTypography-root': { fontFamily: fundingFont },
  },
  groupBlock: {
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    width: '100%',
    padding: 16,
    border: `1px solid ${C.divider}`,
    borderRadius: 8,
    backgroundColor: C.cardBg,
  },
  groupHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    minWidth: 0,
  },
  groupTitle: {
    margin: 0,
    ...fundingType.sectionTitle,
    color: C.strongText,
    minWidth: 0,
  },
  groupCollapseButton: {
    flexShrink: 0,
    width: 24,
    height: 24,
    padding: 0,
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: C.mutedText,
    '& svg': { fontSize: 14 },
  },
  gridFields: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    columnGap: 16,
    rowGap: 16,
    width: '100%',
    '@media (max-width: 720px)': {
      gridTemplateColumns: '1fr',
    },
  },
  empty: {
    margin: 0,
    fontFamily: fundingFont,
    fontSize: 14,
    lineHeight: '20px',
    color: C.mutedText,
  },
  row: {
    display: 'grid',
    gridTemplateColumns: 'minmax(140px, 220px) 1fr',
    gap: 16,
    alignItems: 'flex-start',
    width: '100%',
  },
  labelCol: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    paddingTop: 10,
  },
  label: {
    ...fundingType.bodyMedium,
    color: C.darkText,
  },
  group: {
    ...fundingType.helper,
    color: C.mutedText,
  },
  controlCol: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    minWidth: 0,
  },
  input: {
    ...fundingFormFieldBox,
    width: '100%',
    padding: '10px 16px',
    border: `1px solid ${C.buttonBorder}`,
    borderRadius: 4,
    backgroundColor: C.cardBg,
    fontFamily: fundingFont,
    fontSize: 14,
    fontWeight: 400,
    lineHeight: '20px',
    letterSpacing: '0.25px',
    color: C.darkText,
    outline: 'none',
    '&:disabled': {
      backgroundColor: C.metricBg,
      color: C.mutedText,
    },
    '&::placeholder': { color: C.mutedText },
  },
  select: {
    ...fundingFormFieldBox,
    width: '100%',
    padding: '10px 16px',
    border: `1px solid ${C.buttonBorder}`,
    borderRadius: 4,
    backgroundColor: C.cardBg,
    fontFamily: fundingFont,
    fontSize: 14,
    fontWeight: 400,
    lineHeight: '20px',
    color: C.darkText,
    outline: 'none',
    '&:disabled': {
      backgroundColor: C.metricBg,
      color: C.mutedText,
    },
  },
  gridField: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0,
    minWidth: 0,
  },
  outlinedField: {
    ...fundingFormFieldBox,
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 16px',
    border: `1px solid ${C.buttonBorder}`,
    borderRadius: 4,
    backgroundColor: C.cardBg,
  },
  outlinedFieldError: {
    borderColor: C.errorText,
  },
  outlinedInput: {
    flex: '1 1 auto',
    minWidth: 0,
    height: '100%',
    border: 'none',
    outline: 'none',
    background: 'none',
    fontFamily: fundingFont,
    fontSize: 14,
    fontWeight: 400,
    lineHeight: '20px',
    letterSpacing: '0.25px',
    color: C.darkText,
    '&:disabled': { color: C.mutedText },
    '&::placeholder': { color: C.mutedText },
  },
  outlinedSelect: {
    flex: '1 1 auto',
    minWidth: 0,
    height: '100%',
    border: 'none',
    outline: 'none',
    background: 'none',
    fontFamily: fundingFont,
    fontSize: 14,
    fontWeight: 400,
    lineHeight: '20px',
    color: C.darkText,
    appearance: 'none' as const,
    cursor: 'pointer',
    '&:disabled': { color: C.mutedText },
  },
  floatingLabel: {
    ...fundingType.fieldLabel,
    color: C.mutedText,
    margin: 0,
  },
  floatField: {
    ...fundingFormFieldBox,
    position: 'relative' as const,
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 16px',
    border: `1px solid ${C.buttonBorder}`,
    borderRadius: 4,
    backgroundColor: C.cardBg,
    '&:focus-within': {
      borderColor: C.linkBlue,
    },
  },
  floatFieldError: {
    borderColor: C.errorText,
  },
  // Label on border notch — keep padding symmetric so value stays vertically centered.
  floatFieldFloated: {},
  floatLabelInField: {
    position: 'absolute' as const,
    left: 16,
    top: '50%',
    transform: 'translateY(-50%)',
    margin: 0,
    pointerEvents: 'none' as const,
    transition: 'top 120ms ease, transform 120ms ease, font-size 120ms ease',
    ...fundingType.fieldLabel,
    fontSize: 14,
    color: C.mutedText,
    backgroundColor: C.cardBg,
    padding: '0 2px',
  },
  floatLabelFloated: {
    top: 0,
    transform: 'translateY(-50%)',
    fontSize: 12,
    lineHeight: '16px',
  },
  dateInput: {
    minHeight: 20,
    lineHeight: '20px',
    colorScheme: 'light',
  },
  removeFieldButton: {
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    background: 'transparent',
    padding: 2,
    margin: 0,
    cursor: 'pointer',
    color: C.mutedText,
    fontSize: 12,
    lineHeight: 1,
    '&:hover': { color: C.darkText },
    '&:disabled': { cursor: 'not-allowed', opacity: 0.5 },
  },
  selectTrailing: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    flexShrink: 0,
    alignSelf: 'stretch',
  },
  infoIcon: {
    flexShrink: 0,
    fontSize: 14,
    lineHeight: '14px',
    letterSpacing: '0.15px',
    color: C.mutedText,
  },
  fieldInfoButton: {
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    background: 'transparent',
    padding: 2,
    margin: 0,
    cursor: 'help',
    color: C.mutedText,
    lineHeight: 1,
    '&:hover': { color: C.darkText },
  },
  fieldTrailing: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    flexShrink: 0,
    alignSelf: 'stretch',
  },
  fieldDivider: {
    width: 1,
    alignSelf: 'stretch',
    backgroundColor: C.divider,
    borderRadius: 4,
  },
  caretIcon: {
    fontSize: 16,
    color: C.mutedText,
  },
  helperText: {
    padding: '4px 10px 0 16px',
    fontFamily: fundingFont,
    fontSize: 12,
    fontWeight: 400,
    lineHeight: '16px',
    color: C.mutedText,
    margin: 0,
  },
  errorText: {
    padding: '4px 10px 0 16px',
    fontFamily: fundingFont,
    fontSize: 12,
    fontWeight: 400,
    lineHeight: '16px',
    color: C.errorText,
    margin: 0,
  },
  boolFloatField: {
    ...fundingFormFieldBox,
    alignItems: 'center',
  },
  radioRow: {
    display: 'inline-flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 20,
    minHeight: 24,
    flex: '1 1 auto',
    minWidth: 0,
  },
  radioOption: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    margin: 0,
    cursor: 'pointer',
    fontFamily: fundingFont,
    fontSize: 14,
    fontWeight: 400,
    lineHeight: '20px',
    color: C.darkText,
    '& input': {
      margin: 0,
      width: 16,
      height: 16,
      accentColor: C.linkBlue,
      flexShrink: 0,
    },
    '&:has(input:disabled)': {
      cursor: 'not-allowed',
      opacity: 0.6,
    },
  },
}))
